"use client"

import { ChevronRight, ExternalLink } from "lucide-react"
import Navbar from "@/app/Components/NavbarSection"
import Footer from "@/app/Sections/Footer"

type SitemapLink = {
    name: string;
    href: string;
    external?: boolean;
};

type SitemapGroup = {
    title: string;
    links: SitemapLink[];
};

const sitemapGroups: SitemapGroup[] = [
    {
        title: "Main Pages",
        links: [
            { name: "Home", href: "/" },
            { name: "About Us", href: "/about-us" },
            { name: "All Games", href: "/games" },
            { name: "Coming Soon", href: "/coming-soon" },
        ]
    },
    {
        title: "Game Servers",
        links: [
            { name: "Minecraft Servers", href: "/minecraft-servers" },
            { name: "Hytale Servers", href: "/hytale-servers" },
            { name: "MTA Servers", href: "/mta-servers" },
            { name: "FiveM Servers", href: "/fivem-servers" },
            { name: "Rust Servers", href: "/rust-servers" },
        ]
    },
    {
        title: "Platform",
        links: [
            { name: "Aurex Spark Game Panel", href: "/game-panel" },
            { name: "Hardware & Locations", href: "/hardware" },
        ]
    },
    {
        title: "Legal",
        links: [
            { name: "Privacy Policy", href: "/privacy" },
            { name: "Terms of Service", href: "/tos" },
            { name: "Refund Policy", href: "/refund-policy" },
            { name: "Cookie Policy", href: "/cookie-policy" },
            { name: "Partner Agreement", href: "/partner-agreement" },
        ]
    },
    {
        title: "External Portals",
        links: [
            { name: "Client Dashboard", href: "https://dash.aurex.lk/", external: true },
            { name: "Marketplace", href: "https://dash.aurex.lk/login", external: true },
            { name: "Blog", href: "https://dash.aurex.lk/blog", external: true },
            { name: "Status Page", href: "https://status.aurex.lk/", external: true },
            { name: "Submit Ticket", href: "https://dash.aurex.lk/tickets", external: true },
            { name: "Discord Server", href: "https://discord.com/invite/aurex", external: true },
        ]
    }
]

export default function SitemapPage() {
    return (
        <div className="bg-black min-h-screen">
            <Navbar />
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: "url('https://cdn.aurex.lk/assets/hero-mc.webp')",
                            backgroundSize: "cover",
                            backgroundPosition: "center top",
                            filter: "blur(6px) brightness(0.45)",
                            transform: "scale(1.05)",
                        }}
                    />
                    <div className="absolute inset-0 bg-[#3b0d6e]/40" />
                    <div
                        className="absolute inset-0"
                        style={{ background: "radial-gradient(ellipse 90% 60% at 50% 110%, #000000 0%, transparent 90%)" }}
                    />
                    <div
                        className="absolute top-0 left-0 right-0 h-40 pointer-events-none"
                        style={{ background: "linear-gradient(to bottom, #000000, transparent)" }}
                    />
                </div>

                <div className="relative z-10">
                    <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-12 pb-24">
                        <nav className="flex items-center gap-1.5 text-xs font-poppins text-gray-500 mb-8">
                            <a href="/" className="hover:text-white transition-colors">Home</a>
                            <ChevronRight className="w-3 h-3" />
                            <span className="text-white">Sitemap</span>
                        </nav>
                        <h1 className="font-akira text-3xl sm:text-4xl xl:text-5xl text-white leading-tight tracking-wide mb-3">
                            Site Map
                        </h1>
                        <p className="text-gray-500 font-poppins text-sm">A complete overview of Aurex Hosting URLs</p>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ lineHeight: 0 }}>
                        <svg width="100%" height="50" viewBox="0 0 2598 150" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMaxYMax meet" style={{ display: "block", position: "relative", zIndex: 20 }}>
                            <path d="M2598 150H0L230.359 0H2598V150Z" fill="black" />
                        </svg>
                    </div>
                </div>
            </div>

            <div className="relative z-10 w-full bg-black">
                <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 relative">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
                        {sitemapGroups.map((group, index) => (
                            <div key={index} className="flex flex-col gap-4">
                                <h2 className="font-akira text-[#8B5CF6] text-lg tracking-wider mb-2">
                                    {group.title}
                                </h2>
                                <ul className="flex flex-col gap-3">
                                    {group.links.map((link, i) => (
                                        <li key={i}>
                                            <a 
                                                href={link.href} 
                                                target={link.external ? "_blank" : "_self"}
                                                rel={link.external ? "noopener noreferrer" : ""}
                                                className="group inline-flex items-center gap-2 text-gray-400 hover:text-white font-poppins text-sm transition-colors duration-200"
                                            >
                                                {link.name}
                                                {link.external && <ExternalLink className="w-3 h-3 text-gray-600 group-hover:text-[#8B5CF6] transition-colors" />}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    )
}
